import { useEffect, useRef } from "react";

/**
 * Global ambient edge-glow overlay.
 * - Soft brand-color glow on all 4 screen edges (top, bottom, left, right)
 * - Colors flow continuously by lerping between adjacent sections based on
 *   how far the viewport center has scrolled between their centers
 * - Intensity boosts during active scroll, decays softly when idle
 * - Pointer-events-none, never blocks interaction
 */

// Section id → [primary RGB, secondary RGB] tuples
const SECTION_PALETTE = {
  home:      [[0, 240, 255],  [255, 0, 85]],   // cyan + magenta
  studio:    [[255, 0, 85],   [163, 149, 195]], // magenta + lavender
  packages:  [[0, 240, 255],  [163, 149, 195]], // cyan + lavender
  spotlight: [[255, 0, 85],   [255, 184, 0]],   // magenta + amber
  contact:   [[0, 240, 255],  [163, 149, 195]], // cyan + lavender
};
const SECTION_ORDER = ["home", "studio", "packages", "spotlight", "contact"];
const FALLBACK = SECTION_PALETTE.home;

const BASELINE_INTENSITY = 0.14;
const BASELINE_INTENSITY_DESKTOP = 0.26;
const MAX_INTENSITY = 0.55;
const MAX_INTENSITY_DESKTOP = 0.8;
const DECAY_PER_FRAME = 0.008;
const COLOR_LERP_FACTOR = 0.08; // per-frame easing toward target color (lower = smoother)

const isDesktop = () =>
  typeof window !== "undefined" && window.innerWidth >= 768;

const lerp = (a, b, t) => a + (b - a) * t;

const rgbStr = (c, alpha) =>
  `rgba(${Math.round(c[0])}, ${Math.round(c[1])}, ${Math.round(c[2])}, ${alpha.toFixed(3)})`;

// Compute target [primary, secondary] palette by blending neighboring section centers.
const computeTargetPalette = () => {
  const vh = window.innerHeight;
  const centerY = window.scrollY + vh / 2;

  // Build list of (id, sectionCenterAbsoluteY)
  const points = [];
  for (const id of SECTION_ORDER) {
    const el = document.getElementById(id);
    if (!el) continue;
    const r = el.getBoundingClientRect();
    const absTop = r.top + window.scrollY;
    points.push({ id, center: absTop + r.height / 2 });
  }
  if (points.length === 0) return FALLBACK;

  // Before first or after last
  if (centerY <= points[0].center) return SECTION_PALETTE[points[0].id] || FALLBACK;
  if (centerY >= points[points.length - 1].center) {
    return SECTION_PALETTE[points[points.length - 1].id] || FALLBACK;
  }

  // Find bracketing pair
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (centerY >= a.center && centerY <= b.center) {
      const t = (centerY - a.center) / (b.center - a.center);
      const pa = SECTION_PALETTE[a.id] || FALLBACK;
      const pb = SECTION_PALETTE[b.id] || FALLBACK;
      const primary = [
        lerp(pa[0][0], pb[0][0], t),
        lerp(pa[0][1], pb[0][1], t),
        lerp(pa[0][2], pb[0][2], t),
      ];
      const secondary = [
        lerp(pa[1][0], pb[1][0], t),
        lerp(pa[1][1], pb[1][1], t),
        lerp(pa[1][2], pb[1][2], t),
      ];
      return [primary, secondary];
    }
  }
  return FALLBACK;
};

const ScrollEdgeGlow = () => {
  // DOM refs for each glow layer — we paint directly per frame
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const cornerTLRef = useRef(null);
  const cornerBRRef = useRef(null);

  // Animated state stored in refs to avoid re-render thrash
  const stateRef = useRef({
    primary: [...FALLBACK[0]],
    secondary: [...FALLBACK[1]],
    targetPrimary: [...FALLBACK[0]],
    targetSecondary: [...FALLBACK[1]],
    intensity: BASELINE_INTENSITY,
    targetIntensity: BASELINE_INTENSITY,
    lastScrollY: 0,
    rafId: null,
    idleSince: performance.now(),
    desktop: isDesktop(),
  });

  useEffect(() => {
    // Initial target palette
    const [p, s] = computeTargetPalette();
    stateRef.current.primary = [...p];
    stateRef.current.secondary = [...s];
    stateRef.current.targetPrimary = [...p];
    stateRef.current.targetSecondary = [...s];
    // Initial intensity per device
    const base = stateRef.current.desktop
      ? BASELINE_INTENSITY_DESKTOP
      : BASELINE_INTENSITY;
    stateRef.current.intensity = base;
    stateRef.current.targetIntensity = base;

    const paint = () => {
      const st = stateRef.current;
      const baseline = st.desktop ? BASELINE_INTENSITY_DESKTOP : BASELINE_INTENSITY;
      // Ease current colors toward target
      for (let i = 0; i < 3; i++) {
        st.primary[i] = lerp(st.primary[i], st.targetPrimary[i], COLOR_LERP_FACTOR);
        st.secondary[i] = lerp(st.secondary[i], st.targetSecondary[i], COLOR_LERP_FACTOR);
      }
      // Ease intensity toward target (or decay toward baseline when idle)
      const now = performance.now();
      if (now - st.idleSince > 350) {
        st.targetIntensity = Math.max(
          baseline,
          st.targetIntensity - DECAY_PER_FRAME,
        );
      }
      st.intensity = lerp(st.intensity, st.targetIntensity, 0.12);

      // Desktop gets stronger edge multipliers for a more prominent glow.
      const i = st.intensity;
      const edgeMul = st.desktop ? 0.42 : 0.28;
      const sideMul = st.desktop ? 0.34 : 0.22;
      const cornerMul = st.desktop ? 0.3 : 0.18;
      // Paint
      if (topRef.current) {
        topRef.current.style.background = `linear-gradient(to bottom, ${rgbStr(
          st.primary,
          i * edgeMul,
        )} 0%, transparent 100%)`;
      }
      if (bottomRef.current) {
        bottomRef.current.style.background = `linear-gradient(to top, ${rgbStr(
          st.secondary,
          i * edgeMul,
        )} 0%, transparent 100%)`;
      }
      if (leftRef.current) {
        leftRef.current.style.background = `linear-gradient(to right, ${rgbStr(
          st.primary,
          i * sideMul,
        )} 0%, transparent 100%)`;
      }
      if (rightRef.current) {
        rightRef.current.style.background = `linear-gradient(to left, ${rgbStr(
          st.secondary,
          i * sideMul,
        )} 0%, transparent 100%)`;
      }
      if (cornerTLRef.current) {
        cornerTLRef.current.style.background = `radial-gradient(circle, ${rgbStr(
          st.primary,
          i * cornerMul,
        )} 0%, transparent 75%)`;
      }
      if (cornerBRRef.current) {
        cornerBRRef.current.style.background = `radial-gradient(circle, ${rgbStr(
          st.secondary,
          i * (cornerMul - 0.02),
        )} 0%, transparent 75%)`;
      }

      st.rafId = requestAnimationFrame(paint);
    };
    stateRef.current.rafId = requestAnimationFrame(paint);

    const onScroll = () => {
      const st = stateRef.current;
      const y = window.scrollY;
      const dy = Math.abs(y - st.lastScrollY);
      st.lastScrollY = y;
      st.idleSince = performance.now();

      // Velocity → intensity ceiling (desktop gets a higher ceiling)
      const baseline = st.desktop ? BASELINE_INTENSITY_DESKTOP : BASELINE_INTENSITY;
      const ceiling = st.desktop ? MAX_INTENSITY_DESKTOP : MAX_INTENSITY;
      const boost = Math.min(baseline + dy / 220, ceiling);
      st.targetIntensity = Math.max(st.targetIntensity, boost);

      // Continuous palette interpolation based on scroll position
      const [p, s] = computeTargetPalette();
      st.targetPrimary = p;
      st.targetSecondary = s;
    };

    const onResize = () => {
      stateRef.current.desktop = isDesktop();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (stateRef.current.rafId) cancelAnimationFrame(stateRef.current.rafId);
    };
  }, []);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0"
      data-testid="scroll-edge-glow"
    >
      <div
        ref={topRef}
        className="absolute inset-x-0 top-0 h-14 md:h-32"
        style={{ mixBlendMode: "screen" }}
      />
      <div
        ref={bottomRef}
        className="absolute inset-x-0 bottom-0 h-14 md:h-32"
        style={{ mixBlendMode: "screen" }}
      />
      <div
        ref={leftRef}
        className="absolute inset-y-0 left-0 w-10 md:w-28"
        style={{ mixBlendMode: "screen" }}
      />
      <div
        ref={rightRef}
        className="absolute inset-y-0 right-0 w-10 md:w-28"
        style={{ mixBlendMode: "screen" }}
      />
      <div
        ref={cornerTLRef}
        className="absolute -top-10 -left-10 w-40 h-40 md:w-72 md:h-72 rounded-full"
        style={{ filter: "blur(32px)", mixBlendMode: "screen" }}
      />
      <div
        ref={cornerBRRef}
        className="absolute -bottom-10 -right-10 w-40 h-40 md:w-72 md:h-72 rounded-full"
        style={{ filter: "blur(32px)", mixBlendMode: "screen" }}
      />
    </div>
  );
};

export default ScrollEdgeGlow;
